import React, { useMemo } from 'react';
import { ScannerData } from '../../types/ScannerData';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTradingCall } from '../../contexts/TradingCallContext';
import { formatMoney, formatLegLines } from '../../utils/paperTradingFormat';
import { isDesignSystemOn } from '../../theme/mmecDsPreview';
import ToolState from '../mmec/ToolState';

interface TradingCallsWidgetProps {
  data: ScannerData[];
  isDashboard?: boolean;
  dataMode?: 'accumulated' | '5min';
  maxCalls?: number;
}

const TradingCallsWidget: React.FC<TradingCallsWidgetProps> = ({
  data,
  isDashboard = false,
  dataMode = 'accumulated',
  maxCalls = 10
}) => {
  const { language } = useLanguage();
  useTheme();
  const { activeCalls, closedCalls } = useTradingCall();
  
  const translations = {
    title: language === 'ar' ? 'التداول التجريبي' : 'Paper Trading',
    noCalls: language === 'ar' ? 'لا توجد صفقات تجريبية' : 'No paper positions',
    active: language === 'ar' ? 'نشط' : 'Active',
    closed: language === 'ar' ? 'مغلق' : 'Closed',
    profit: language === 'ar' ? 'ربح' : 'Profit',
    loss: language === 'ar' ? 'خسارة' : 'Loss',
    pending: language === 'ar' ? 'في الانتظار' : 'Pending',
    time: language === 'ar' ? 'الوقت' : 'Time',
    debit: language === 'ar' ? 'المدفوع (علاوة)' : 'Debit (premium)',
    mark: language === 'ar' ? 'تسعير حالي' : 'Mark',
    tp: language === 'ar' ? 'هدف (علاوة)' : 'Take-profit (mark)',
    sl: language === 'ar' ? 'وقف (علاوة)' : 'Stop (mark)',
    underlyingRef: language === 'ar' ? 'مرجع السعر للأصل' : 'Underlying reference',
    updated: language === 'ar' ? 'آخر تحديث للتسعير' : 'Mark updated',
    legs: language === 'ar' ? 'الأرجل' : 'Legs',
  };

  const recentCalls = useMemo(() => {
    const allCalls = [...activeCalls, ...closedCalls];
    return allCalls
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, maxCalls);
  }, [activeCalls, closedCalls, maxCalls]);

  // Design System preview (artboard web/trading-calls-widget): neutral surface-2 cards, verdict as a Tag.
  const ds = isDesignSystemOn();
  const lang = language === 'ar' ? 'ar' : 'en';

  if (recentCalls.length === 0) {
    if (ds) return <ToolState kind="empty" lang={lang} title={translations.noCalls} body="" fill />;
    return (
      <div className="flex items-center justify-center h-full" style={{ color: 'var(--muted)', fontSize: 13 }}>
        {translations.noCalls}
      </div>
    );
  }

  const isClosedStatus = (s: string) =>
    ['TARGET_HIT', 'STOP_LOSS', 'EXPIRED', 'CANCELLED', 'MANUAL_CLOSE'].includes(s);

  const getCallStatusColor = (call: { status: string; performance?: { pnl?: number } }): React.CSSProperties => {
    if (ds) return { background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)' };
    if (isClosedStatus(call.status)) {
      const pnl = call.performance?.pnl ?? 0;
      if (pnl > 0) return { borderLeft: '3px solid var(--up)', background: 'var(--up-dim)' };
      if (pnl < 0) return { borderLeft: '3px solid var(--down)', background: 'var(--down-dim)' };
      return { borderLeft: '3px solid var(--border)', background: 'var(--elev2)' };
    }
    return { borderLeft: '3px solid var(--acc)', background: 'var(--acc-dim)' };
  };

  const getCallStatusText = (call: { status: string }) => {
    if (isClosedStatus(call.status)) {
      if (call.status === 'TARGET_HIT') return { text: translations.profit, color: 'var(--up)' };
      if (call.status === 'STOP_LOSS') return { text: translations.loss, color: 'var(--down)' };
      return { text: call.status.replace(/_/g, ' '), color: 'var(--muted)' };
    }
    return { text: translations.active, color: 'var(--acc)' };
  };

  return (
    <div className={`w-full h-full flex flex-col${ds ? ' mmec' : ''}`} dir={ds ? (lang === 'ar' ? 'rtl' : 'ltr') : undefined}>
      {/* Header */}
      <div className="p-2 border-b" style={{ borderColor: 'var(--hair)' }}>
        <h3 className="text-sm font-semibold" style={{ color: 'var(--txt)' }}>
          {translations.title} ({activeCalls.length} {translations.active})
        </h3>
      </div>
      
      {/* Calls List */}
      <div className="flex-1 overflow-y-auto p-2">
        <div className="space-y-2">
          {recentCalls.map((call) => {
            const statusInfo = getCallStatusText(call);
            const legLbl =
              language === 'ar'
                ? ({ long: 'شراء', short: 'بيع', call: 'خيار الشراء', put: 'خيار البيع' } as const)
                : undefined;
            const legLines = formatLegLines(call, legLbl);
            return (
              <div
                key={call.id}
                className="p-3 rounded-lg text-xs"
                style={{ ...getCallStatusColor(call), color: 'var(--txt)' }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium">
                        {call.symbol || call.underlying || 'Unknown'}
                      </span>
                      {ds ? (
                        <span className={`mmec-tag ${call.status === 'TARGET_HIT' ? 'mmec-tag--up' : call.status === 'STOP_LOSS' ? 'mmec-tag--down' : isClosedStatus(call.status) ? '' : 'mmec-tag--gold'}`}>
                          {statusInfo.text}
                        </span>
                      ) : (
                      <span className="px-2 py-0.5 rounded text-xs" style={{ color: statusInfo.color, background: 'var(--elev)', fontWeight: 500 }}>
                        {statusInfo.text}
                      </span>
                      )}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--muted)' }}>
                      {call.strategy.type || 'Trading Call'}
                    </div>
                    <div className="text-xs mt-1 space-y-0.5" style={{ color: 'var(--txt-2)', fontVariantNumeric: 'tabular-nums', fontFamily: ds ? 'var(--font-mono)' : undefined }}>
                      <div>
                        {translations.debit}: ${formatMoney(call.entryPrice)}
                        {call.performance?.currentPrice != null && (
                          <>
                            {' '}
                            · {translations.mark}: ${formatMoney(call.performance.currentPrice)}
                          </>
                        )}
                        {call.performance?.pnl != null && (
                          <span
                            className={
                              ''
                            }
                            style={{ color: call.performance.pnl >= 0 ? 'var(--up-text, var(--up))' : 'var(--down-text, var(--down))' }}
                          >
                            {' '}
                            (P&amp;L ${formatMoney(call.performance.pnl)})
                          </span>
                        )}
                      </div>
                      <div>
                        {translations.tp}: ${formatMoney(call.targetPrice)} · {translations.sl}: $
                        {formatMoney(call.stopLoss)}
                      </div>
                      {call.strategy.details.targetUnderlyingPrice != null &&
                        call.strategy.details.stopUnderlyingPrice != null && (
                          <div>
                            {translations.underlyingRef}: ~${formatMoney(call.strategy.details.targetUnderlyingPrice)}{' '}
                            / ~${formatMoney(call.strategy.details.stopUnderlyingPrice)}
                          </div>
                        )}
                      {legLines.length > 0 && (
                        <div className="mt-1">
                          <span className="font-medium">{translations.legs}: </span>
                          {legLines.join(' · ')}
                        </div>
                      )}
                      {call.performance?.lastUpdate && (
                        <div className="opacity-80">
                          {translations.updated}:{' '}
                          {new Date(call.performance.lastUpdate).toLocaleString()}
                        </div>
                      )}
                    </div>
                    {call.reasoning && (
                      <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                        {call.reasoning}
                      </div>
                    )}
                  </div>
                  <div className="text-xs ml-2 text-right" style={{ color: 'var(--muted)', fontVariantNumeric: 'tabular-nums', fontFamily: ds ? 'var(--font-mono)' : undefined }}>
                    <div>{translations.time}</div>
                    <div>{new Date(call.timestamp).toLocaleString()}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TradingCallsWidget; 